import { useEffect, useRef, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';

import { useTranslation } from 'react-i18next';
import {
  clearMapEvent,
  startDrawPropertyWithPolygonEvent,
  centerCoordinatesEvent,
  clearDrawPolygonMapEvent,
  polygonDrawnEvent,
} from '../map/MapComponent';
import { EventBus } from 'ts-bus';
import {
  UserSettings,
  getReadableSize,
  convertFromSquareMeter,
} from '../../server/model/user/UserSettings';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { defaultStyles } from '../../styling/styles';
import {
  SECONDARY_TEXT_COLOR_ON_WHITE,
  TEXT_COLOR_ERROR,
} from '../../styling/colors';
import { TEXT_SIZE_SMALL } from '../../styling/dimens';
import { fetchBackend } from '../../authentication';
import { SHOW_ERROR_INFO_TO_USER } from '../../util/AppComponentsProps';
import { OfficialPropertyInputDataMap } from './model/OfficialPropertyInputDataMap';
import { OfficialPropertyInputData } from './model/OfficialPropertyInputData';
import { OfficialPropertySelectedDataMap } from './model/OfficialPropertySelectedDataMap';

export type AddNewPropertyViaOfficialDataComponentProps = {
  eventBus: EventBus;
  sendingCreatePropertyRequest: boolean;
  userSettings: UserSettings;
};

let subscribedEvents: { (): void }[] = [];

export default function AddNewPropertyViaOfficialDataComponent(
  addNewPropertyViaOfficialDataComponentProps: AddNewPropertyViaOfficialDataComponentProps
) {
  const [info, setInfo] = useState<string>('');
  const [neededInputData, setNeededInputData] = useState<OfficialPropertyInputDataMap | undefined>(undefined);
  const [officialSizeInSquareMeter, setOfficialSizeInSquareMeter] = useState<number>(0);
  const [
      sendingRequestOfficalDataRequest,
      setSendingRequestOfficalDataRequest,
    ] = useState<boolean>(false);
  const { t } = useTranslation();

  // At start clear once the map
  useEffect(() => {
    requestOfficialData()
    addNewPropertyViaOfficialDataComponentProps.eventBus.publish(clearMapEvent({}));
  }, [addNewPropertyViaOfficialDataComponentProps.eventBus]);

  async function requestOfficialData() {
      setSendingRequestOfficalDataRequest(true);

      const data: OfficialPropertySelectedDataMap = {}
      if (neededInputData) {
        Object.entries(neededInputData).forEach(([key, officialRequestedData]) => {
          if (key && officialRequestedData.selectedData !== undefined) {
            data[key] = officialRequestedData.selectedData
          }
        })
      }

      const requestOptions = {
        method: 'POST',
        body: JSON.stringify(data),
      };
      const response = await fetchBackend(
        'secure/officialPropertySearch/requestOfficialData',
        requestOptions
      );

      setSendingRequestOfficalDataRequest(true);

      if (response.error) {
        setInfo(response.error);
  
        // Remove info text after some time
        setTimeout(function () {
          setInfo('');
        }, SHOW_ERROR_INFO_TO_USER);
      } else {
        setInfo('');
      }

      if (Array.isArray(response.neededInputData)) {
        // The backend always returns the complete set of needed input data including
        // the values which already have been given, so the map is rebuilt from scratch.
        const dataMap: OfficialPropertyInputDataMap = {}
        response.neededInputData.forEach((dataToAsk: { parameterName: string; neededDataInputType: any; availableData: any; caption: string; givenValidData: boolean; selectedData?: string; }) => {
          const officialPropertyInputData: OfficialPropertyInputData = {
            neededDataInputType: dataToAsk.neededDataInputType,
            availableData: dataToAsk.availableData ?? [],
            caption: dataToAsk.caption,
            givenValidData: dataToAsk.givenValidData,
            selectedData: dataToAsk.selectedData ?? undefined
          }
          dataMap[dataToAsk.parameterName] = officialPropertyInputData;
        });

        setNeededInputData(dataMap);
      }
  
      setSendingRequestOfficalDataRequest(false);
      /*if (response.result === false) {
        setSendingRequestOfficalDataRequest(false);
        // setInfo(response.message);
  
        // Remove info text after some time
        /*setTimeout(function () {
          setInfo('');
        }, SHOW_ERROR_INFO_TO_USER);
      }*/
  
      if (response.result) {
        const coordinates = response.result.coordinates.map((latLng: { longitude: number; latitude: number; }) => {
          return {
            latitude: latLng.latitude,
            longitude: latLng.longitude,
          }
        });

        setOfficialSizeInSquareMeter(response.result.officialSizeInSquareMeter ?? 0);

        addNewPropertyViaOfficialDataComponentProps.eventBus.publish(
          centerCoordinatesEvent({
            switchToMapViewOnMobileDevice: true,
            coordinates: coordinates,
          })
        );

        addNewPropertyViaOfficialDataComponentProps.eventBus.publish(
          startDrawPropertyWithPolygonEvent({
            polygon:
              coordinates,
          })
        );

        // The polygon is drawn programmatically, so the map does not emit the
        // event itself. Inform the listeners about the now available coordinates.
        addNewPropertyViaOfficialDataComponentProps.eventBus.publish(
          polygonDrawnEvent({ coordinates: [...coordinates] })
        );
      } else {
        // No result available (anymore): erase the previously drawn polygon and its size
        setOfficialSizeInSquareMeter(0);
        addNewPropertyViaOfficialDataComponentProps.eventBus.publish(
          clearMapEvent({})
        );

        addNewPropertyViaOfficialDataComponentProps.eventBus.publish(
          clearDrawPolygonMapEvent({})
        );

        addNewPropertyViaOfficialDataComponentProps.eventBus.publish(
          polygonDrawnEvent({ coordinates: [] })
        );
      }
    }

  const officialSize = convertFromSquareMeter(
    officialSizeInSquareMeter,
    addNewPropertyViaOfficialDataComponentProps.userSettings.preferredAreaType
  );
  const userReadableOfficialSize = getReadableSize(
    officialSize,
    addNewPropertyViaOfficialDataComponentProps.userSettings.preferredAreaType,
    t
  );

  return (
    <div>
      {
        neededInputData ?
          <NeededDataParts
            data={neededInputData}
            sendingData={sendingRequestOfficalDataRequest}
            requestDataAgain={() => requestOfficialData()}/>
          : null
      }
      <div className={css(styles.smallSpacing)} />
      {info ? (
        <div className={css(styles.errorText)}>{info}</div>
      ) : null}
      <div className={css(styles.smallSpacing)} />
      
      {officialSizeInSquareMeter === 0 ? null : (
        <div className={css(styles.caption)}>
          {t('PROPERTIES.ADD_PROPERTY.RETRIEVED_SIZE') + userReadableOfficialSize}
        </div>
      )}
      <div className={css(styles.smallSpacing)} />
      <div className={css(styles.smallSpacing)} />
      
      <div className={css(styles.spacing)} />
    </div>
  );
}

export type NeededDataPartsProps = {
  data: OfficialPropertyInputDataMap;
  sendingData: boolean;
  requestDataAgain: () => {};
};

function NeededDataParts(
  neededDataPartsProps: NeededDataPartsProps,
) {
  return Object.entries(neededDataPartsProps.data).map(([key, officialRequestedData]) =>  {
    if (officialRequestedData.neededDataInputType == 'SELECTION') {
      return <div key={key}>
        <FormControl fullWidth sx={{ paddingTop: 0, paddingBottom: 2 }}>
          <InputLabel id={`${key}-label`}>{officialRequestedData.caption}</InputLabel>
          <Select
            labelId={`${key}-label`}
            label={officialRequestedData.caption}
            disabled={neededDataPartsProps.sendingData}
            value={officialRequestedData.selectedData ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              officialRequestedData.selectedData = value;
              neededDataPartsProps.requestDataAgain();
            }}
          >
            {officialRequestedData.availableData.map(item => {
              return(
                <MenuItem key={item.id} value={item.id}>
                  {item.visibleName}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
      </div>
    }

    if (officialRequestedData.neededDataInputType == 'STRING') {
      const debounceRef = useRef<NodeJS.Timeout | null>(null); 
      return <div key={key}>
        <TextField
          defaultValue={officialRequestedData.selectedData ?? ''}
          onChange={(input) => {
            const value = input.target.value;
            officialRequestedData.selectedData = value;
            
            if (debounceRef.current) {
              clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
              neededDataPartsProps.requestDataAgain();
            }, 500);
          }}
          disabled={neededDataPartsProps.sendingData}
          fullWidth
          label={officialRequestedData.caption}
          sx={{ paddingTop: 0, paddingBottom: 2 }}
        />
      </div>
    }

    return <div/>
  })
}

const styles = StyleSheet.create({
  caption: {
    ...defaultStyles.textStyle,
    fontSize: TEXT_SIZE_SMALL,
    color: SECONDARY_TEXT_COLOR_ON_WHITE,
    alignItems: 'center',
  },
  errorText: {
    ...defaultStyles.textStyle,
    fontSize: TEXT_SIZE_SMALL,
    color: TEXT_COLOR_ERROR,
    alignItems: 'center',
    textAlign: 'center',
  },
  smallSpacing: {
    height: 10,
  },
  spacing: {
    height: 35,
  },
  smallButton: {
    ...defaultStyles.materialSmallButtonStyle,
    fontWeight: 'bold',
    float: 'right',
  },
});
